import { useCalendar } from "./CalendarContext";
import {
  ComponentPropsWithoutRef,
  KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from "react";
import cx from "classnames";
import styles from "./Calendar.module.scss";
import { DateTime, Info } from "luxon";
import {
  isBeyondMaxDate,
  isBeyondMinDate,
  isSameDay,
  isSameMonth,
  isUnavailableDate,
  rangeUpdater,
  toDateString,
} from "./utils";

export type CalendarDayProps = ComponentPropsWithoutRef<"td"> & {
  /**
   * The date to display.
   */
  date: DateTime;
};

/**
 * A component that displays a day in the calendar.
 *
 * @param props.date - The date to display.
 * @param props - The rest of the props for the td element.
 * @returns A component that displays a day in the calendar.
 */
export const CalendarDay = forwardRef<HTMLTableCellElement, CalendarDayProps>(
  (props, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- filtering out className
    const { className, date, ...rest } = props;
    const context = useCalendar();
    const {
      disableAutofocus,
      value,
      setValue,
      focusedDate,
      setFocusedDate,
      hoveredDate,
      setHoveredDate,
      onSelection,
      locale,
      minDate,
      maxDate,
      unavailableDates,
      unavailable,
      defaultTimeZone,
      controlled,
      id: uid,
      range,
      dateFormat,
      startDay,
      setSelectedMonth,
      setSelectedYear,
    } = context;

    const initialized = useRef(false);

    const futureDateSelection = useCallback(
      (targetDate: DateTime) => {
        if (maxDate !== null && targetDate.toMillis() > maxDate?.toMillis()) {
          return;
        }

        if (
          isUnavailableDate(
            targetDate,
            unavailableDates,
            unavailable,
            defaultTimeZone,
          )
        ) {
          let nextFocusDate = targetDate.plus({ days: 1 });
          for (let i = 0; i < 365; i++) {
            if (
              maxDate !== null &&
              nextFocusDate.toMillis() > maxDate?.toMillis()
            ) {
              break;
            }
            if (
              isUnavailableDate(
                nextFocusDate,
                unavailableDates,
                unavailable,
                defaultTimeZone,
              )
            ) {
              nextFocusDate = nextFocusDate.plus({ days: 1 });
            } else {
              // setHoveredDate(nextFocusDate);
              setFocusedDate(nextFocusDate);
              break;
            }
          }
          return;
        }

        setFocusedDate(targetDate);

        if (targetDate.month !== focusedDate.month) {
          setSelectedMonth(targetDate.month);
          if (targetDate.year !== focusedDate.year) {
            setSelectedYear(targetDate.year);
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't need to re-render when the selected month or year changes
      [maxDate, setFocusedDate, defaultTimeZone, unavailableDates, unavailable],
    );

    const pastDateSelection = (targetDate: DateTime) => {
      if (minDate !== null && targetDate.toMillis() < minDate?.toMillis()) {
        return;
      }

      if (
        isUnavailableDate(
          targetDate,
          unavailableDates,
          unavailable,
          defaultTimeZone,
        )
      ) {
        let prevFocusDate = targetDate.minus({ days: 1 });
        for (let i = 0; i < 365; i++) {
          if (
            minDate !== null &&
            prevFocusDate.toMillis() < minDate?.toMillis()
          ) {
            break;
          }
          if (
            isUnavailableDate(
              prevFocusDate,
              unavailableDates,
              unavailable,
              defaultTimeZone,
            )
          ) {
            prevFocusDate = prevFocusDate.minus({ days: 1 });
          } else {
            // setHoveredDate(prevFocusDate);
            setFocusedDate(prevFocusDate);
            break;
          }
        }
        return;
      }

      setFocusedDate(targetDate);

      if (targetDate.month !== focusedDate.month) {
        setSelectedMonth(targetDate.month);
        if (targetDate.year !== focusedDate.year) {
          setSelectedYear(targetDate.year);
        }
      }
    };

    const isSelectedDate = (date: DateTime, compareDate?: DateTime) => {
      if (!range) {
        return isSameDay(date, compareDate ?? value);
      }
      if (value.start && value.end) {
        return (
          date.toMillis() >= value.start.startOf("day").toMillis() &&
          date.toMillis() <= value.end.endOf("day").toMillis()
        );
      }

      return isSameDay(date, value.start);
    };

    const isRange = (date: DateTime) => {
      if (!range) {
        return false;
      }
      if (value.start && value.end) {
        return (
          date.toMillis() >= value.start.startOf("day").toMillis() &&
          date.toMillis() <= value.end.endOf("day").toMillis() &&
          !isSameDay(value.start, date) &&
          !isSameDay(value.end, date)
        );
      }

      return false;
    };

    const onKeyDownHandler = (e: KeyboardEvent<HTMLButtonElement>) => {
      switch (e.code) {
        case "Space":
        case "Enter":
          e.preventDefault();
          if (!range) {
            if (!controlled) {
              setValue(isSameDay(value, focusedDate) ? null : focusedDate);
            }
            onSelection?.({
              value: isSameDay(value, focusedDate)
                ? undefined
                : (focusedDate
                    .setZone(defaultTimeZone)
                    .startOf("day")
                    .toISO() ?? undefined),
            });
            break;
          }
          rangeUpdater(value, focusedDate, setValue, onSelection);
          break;
        case "ArrowLeft":
          e.preventDefault();
          pastDateSelection(focusedDate.minus({ days: 1 }));
          break;
        case "ArrowRight":
          e.preventDefault();
          futureDateSelection(focusedDate.plus({ days: 1 }));
          break;
        case "ArrowUp":
          e.preventDefault();
          pastDateSelection(focusedDate.minus({ days: 7 }));
          break;
        case "ArrowDown":
          e.preventDefault();
          futureDateSelection(focusedDate.plus({ days: 7 }));
          break;
        case "PageUp":
          e.preventDefault();
          if (e.shiftKey) {
            pastDateSelection(focusedDate.minus({ years: 1 }));
            break;
          }
          pastDateSelection(focusedDate.minus({ months: 1 }));
          break;
        case "PageDown":
          e.preventDefault();
          if (e.shiftKey) {
            futureDateSelection(focusedDate.plus({ years: 1 }));
            break;
          }
          futureDateSelection(focusedDate.plus({ months: 1 }));
          break;

        default:
          break;
      }
    };

    let startDayIndex = 6;
    switch (startDay) {
      case "Monday":
        startDayIndex = 0;
        break;
      case "Sunday":
      default:
        startDayIndex = 6;
    }

    const weekdaysArr: string[] = [];
    for (let i = 0; i < 7; i++) {
      const dayIndex = (startDayIndex + i) % 7;
      weekdaysArr.push(Info.weekdays("narrow", { locale })[dayIndex]);
    }

    const firstDay = focusedDate.startOf("month");

    const prevArr: DateTime<boolean>[] = [];
    if (firstDay.weekday !== startDayIndex + 1) {
      // Complete the first row with the previous month's dates.
      for (let i = 1; i < 8; i++) {
        const prevDay = firstDay.minus({ days: i });
        prevArr.push(prevDay);
        if (prevDay.weekday === startDayIndex + 1) break;
      }
    }
    prevArr.reverse();

    const onClickHandler = (d: DateTime) => {
      setFocusedDate(d);
      if (!range) {
        onSelection?.({
          value: toDateString(d.setZone(defaultTimeZone)) ?? undefined,
        });
        if (!controlled) {
          setValue(d);
        }
        return;
      }

      rangeUpdater(value, d, setValue, onSelection);
    };

    const onFocusHandler = (d: DateTime) => {
      setHoveredDate(d);
    };
    const onBlurHandler = () => {
      setHoveredDate(undefined);
    };

    const onMouseEnterHandler = (d: DateTime) => {
      setHoveredDate(d);
    };

    useEffect(() => {
      if (!initialized.current) {
        initialized.current = true;
        return;
      }
      if (!disableAutofocus) {
        requestAnimationFrame(() => {
          const targetElement =
            document.getElementById(`${uid}-${focusedDate.toISODate()}`) ||
            document.activeElement?.shadowRoot?.getElementById(
              `${uid}-${focusedDate.toISODate()}`,
            );
          targetElement?.focus();
        });
      }
    }, [focusedDate, uid, disableAutofocus]);

    useEffect(() => {
      if (
        !isUnavailableDate(
          focusedDate,
          unavailableDates,
          unavailable,
          defaultTimeZone,
        )
      )
        return;
      futureDateSelection(focusedDate.plus({ days: 1 }));
    }, [
      focusedDate,
      futureDateSelection,
      defaultTimeZone,
      unavailableDates,
      unavailable,
    ]);

    const isDisabled =
      !isSameMonth(date, focusedDate) ||
      isBeyondMinDate(date, minDate) ||
      isBeyondMaxDate(date, maxDate) ||
      isUnavailableDate(date, unavailableDates, unavailable, defaultTimeZone);
    const isUnavailable = isUnavailableDate(
      date,
      unavailableDates,
      unavailable,
      defaultTimeZone,
    );

    const hoverRange =
      range &&
      hoveredDate &&
      value.start &&
      !value.end &&
      date.toMillis() > value.start.toMillis() &&
      date.toMillis() <= hoveredDate.toMillis();

    const cellCx = cx(styles["cell"], {
      [styles["selected"]]: !isUnavailable && isSelectedDate(date),
      [styles["range-first"]]:
        range &&
        value.start &&
        (value.end || hoveredDate) &&
        isSameDay(date, value.start),
      [styles["range-last"]]:
        range &&
        ((value.start && value.end && isSameDay(date, value.end)) ||
          (hoverRange && isSameDay(date, hoveredDate))),
      [styles["range"]]: isRange(date) || hoverRange,
      [styles["is-unavailable"]]: isUnavailable,
      [styles["overflow"]]: !isSameMonth(date, focusedDate),
      [styles["hover-last"]]: hoverRange && isSameDay(date, hoveredDate),
    });
    const isMinDate =
      minDate !== null && date.toMillis() <= minDate?.toMillis();
    const isMaxDate =
      maxDate !== null && date.toMillis() >= maxDate?.toMillis();

    return (
      <td
        key={date.toISODate()}
        aria-disabled={!isSameMonth(date, focusedDate)}
        role="gridcell"
        {...rest}
        ref={ref}
      >
        {!isUnavailable && (isRange(date) || hoverRange) ? (
          <div
            role="presentation"
            className={cx(styles["range-fill"], {
              [styles["range-disabled"]]: isDisabled,
              [styles["hover-last"]]:
                hoverRange && isSameDay(date, hoveredDate),
            })}
          />
        ) : null}
        <button
          onClick={() => onClickHandler(date)}
          onFocus={() => onFocusHandler(date)}
          onBlur={() => onBlurHandler()}
          className={cellCx}
          onKeyDown={onKeyDownHandler}
          onMouseEnter={() => onMouseEnterHandler(date)}
          tabIndex={isSameDay(date, focusedDate) ? 0 : -1}
          disabled={isDisabled ? true : undefined}
          aria-label={`${date.toLocaleString(dateFormat)}${isSelectedDate(date) ? " selected" : ""}${
            isMinDate ? ", You are on the first available date" : ""
          }${isMaxDate ? ", You are on the last available date" : ""}`}
          id={`${uid}-${date.toISODate()}`}
        >
          {date.day}
        </button>
      </td>
    );
  },
);

CalendarDay.displayName = "CalendarDay";
